import React, { useEffect } from "react";
import { TextField } from "components";
import formatDate from "utils/formatDate";
import formatPhoneNum from "utils/formatPhone";
import { isValidDate } from "utils/validDate";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import ButtonV2 from "elements/ButtonV2";

const AdditionalOwner = () => {
	const {
		flowId,
		next,
		maritalStatusValue,
		ownersValue,
		additionalOwnerFirstNameValue,
		setAdditionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		setAdditionalOwnerLastNameValue,
		additionalOwnerEmailValue,
		setAdditionalOwnerEmailValue,
		additionalOwnerPhoneValue,
		setAdditionalOwnerPhoneValue,
		additionalOwnerDobValue,
		setAdditionalOwnerDobValue,
		owner2SsnTin,
		setOwner2SsnTin,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();
	const [matchingOwnerError, setMatchingOwnerError] = React.useState(false);

	const ssnTinLength = React.useMemo(() => {
		if (flowId === "MICHIGAN_EXEMPTION") {
			return 4;
		}

		return 9;
	}, [flowId]);

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Additional_Owner_Answer,
			data: {
				"Additional Owner Details": `${additionalOwnerFirstNameValue}, ${additionalOwnerLastNameValue}, ${additionalOwnerEmailValue}, ${additionalOwnerPhoneValue}, ${additionalOwnerDobValue}`,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			"Additional Owner Details": `${additionalOwnerFirstNameValue}, ${additionalOwnerLastNameValue}, ${additionalOwnerEmailValue}, ${additionalOwnerPhoneValue}, ${additionalOwnerDobValue}`,
		};
		next({ trackingEventData });
	};

	useEffect(() => {
		if (
			details?.auth_last_name.toLowerCase() ===
				additionalOwnerLastNameValue?.toLowerCase() &&
			details?.auth_first_name.toLowerCase() ===
				additionalOwnerFirstNameValue?.toLowerCase()
		) {
			setMatchingOwnerError(true);
		} else {
			setMatchingOwnerError(false);
		}
	}, [
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		details?.auth_first_name,
		details?.auth_last_name,
	]);

	const { questionTitle, questionSubtitle } = React.useMemo(() => {
		if (
			details?.state === "NY" &&
			maritalStatusValue?.includes("Married") &&
			["Own With Spouse", "Sole Owner"].includes(ownersValue ?? "")
		) {
			return {
				questionTitle: "Spouse Details",
				questionSubtitle:
					"The county requires spouse details in order to apply.",
			};
		}

		return {
			questionTitle: "Additional Owner Details",
			questionSubtitle:
				"The county requires all owners to be listed in order to apply.",
		};
	}, [details?.state, ownersValue, maritalStatusValue]);

	const showFirstName = true;
	const showLastName = true;

	const showEmail = React.useMemo(() => {
		return [
			"TEXAS_EXEMPTION",
			"ILLINOIS_EXEMPTION",
			"RESEARCH_EXEMPTION",
		].includes(flowId ?? "");
	}, [flowId]);

	const showPhone = React.useMemo(() => {
		return [
			"TEXAS_EXEMPTION",
			"ILLINOIS_EXEMPTION",
			"RESEARCH_EXEMPTION",
		].includes(flowId ?? "");
	}, [flowId]);

	const showDateOfBirth = React.useMemo(() => {
		return flowId !== "PENNSYLVANIA_EXEMPTION";
	}, [flowId]);

	const showSSN = React.useMemo(() => {
		return [
			"NEW_YORK_EXEMPTION",
			"FLORIDA_EXEMPTION",
			"MICHIGAN_EXEMPTION",
		].includes(flowId ?? "");
	}, [flowId]);

	const buttonDisabled = React.useMemo(() => {
		const conditions = [];
		if (showFirstName) {
			conditions.push(!!additionalOwnerFirstNameValue);
		}

		if (showLastName) {
			conditions.push(!!additionalOwnerLastNameValue);
		}

		if (showEmail) {
			conditions.push(!!additionalOwnerEmailValue);
		}

		if (showPhone) {
			conditions.push(!!additionalOwnerPhoneValue);
		}

		if (showDateOfBirth) {
			conditions.push(additionalOwnerDobValue?.length === 10);
		}

		if (showSSN) {
			conditions.push(owner2SsnTin && owner2SsnTin.length > ssnTinLength - 1);
		}

		return !conditions.every(Boolean);
	}, [
		flowId,
		showFirstName,
		showLastName,
		showEmail,
		showPhone,
		showDateOfBirth,
		showSSN,
		owner2SsnTin,
		ssnTinLength,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		additionalOwnerEmailValue,
		additionalOwnerPhoneValue,
		additionalOwnerDobValue,
	]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>{questionTitle}</p>
				</div>
				<p className="exemption-question-card-subtitle sm">
					{questionSubtitle}
				</p>
				<div className="exemptions-input-flex-container">
					{showFirstName && (
						<TextField
							required
							label="First Name"
							placeholder="First Name"
							containerClassName="exemptions-card-input"
							value={additionalOwnerFirstNameValue ?? ""}
							onChange={e => {
								setAdditionalOwnerFirstNameValue(e.target.value);
							}}
						/>
					)}
					{showLastName && (
						<TextField
							required
							label="Last Name"
							placeholder="Last Name"
							containerClassName="exemptions-card-input"
							value={additionalOwnerLastNameValue ?? ""}
							onChange={e => {
								setAdditionalOwnerLastNameValue(e.target.value);
							}}
						/>
					)}
				</div>
				<div className="exemptions-input-flex-container">
					{showEmail && (
						<TextField
							required
							label="Email"
							placeholder="Email Address"
							containerClassName="exemptions-card-input"
							value={additionalOwnerEmailValue ?? ""}
							onChange={e => {
								setAdditionalOwnerEmailValue(e.target.value);
							}}
						/>
					)}
					{showPhone && (
						<TextField
							required
							type="tel"
							autoComplete="tel-national"
							maxLength={14}
							value={additionalOwnerPhoneValue ?? ""}
							onChange={e => {
								setAdditionalOwnerPhoneValue(formatPhoneNum(e.target.value));
							}}
							label="Phone Number"
							placeholder="Phone Number"
							containerClassName="exemptions-card-input"
						/>
					)}
				</div>
				{showDateOfBirth && (
					<TextField
						required
						label="Date of Birth"
						placeholder="MM/DD/YYYY"
						containerClassName="exemptions-card-input"
						maxLength={10}
						minLength={10}
						value={additionalOwnerDobValue ?? ""}
						onChange={e => {
							const formatted = formatDate(e, additionalOwnerDobValue ?? "");
							const valid = isValidDate(formatted);

							if (valid) {
								setAdditionalOwnerDobValue(formatted);
							}
						}}
					/>
				)}
				{showSSN && (
					<TextField
						required
						label={
							flowId === "MICHIGAN_EXEMPTION"
								? "Last 4 of Social Security Number"
								: "Social Security Number"
						}
						placeholder={Array(ssnTinLength).fill("*").join("")}
						containerClassName="exemptions-card-input"
						maxLength={ssnTinLength}
						minLength={ssnTinLength}
						numeric
						autoComplete="off"
						value={owner2SsnTin ?? ""}
						onChange={event => {
							const { value } = event.target;
							const sanitizedValue = value
								.replace(/[^\d]/g, "")
								.slice(0, ssnTinLength);
							setOwner2SsnTin(sanitizedValue);
						}}
					/>
				)}
			</div>
			{matchingOwnerError && (
				<p className="sm exemptions-notification-message-rust mt-2">
					It appears you're listing the same owner twice. In order to process
					your application correctly with the county, we need accurate second
					owner information. If owner information is inaccurate, the county will
					reject the application for your exemption. To avoid issues and delays,
					please provide the correct second owner information.
				</p>
			)}
			<ButtonV2
				size="large"
				disabled={buttonDisabled || matchingOwnerError}
				type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default AdditionalOwner;
